import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExport,
  faChevronDown,
  faCode,
  faCube,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";

const ExportOptionsDropdown = ({
  features = {},
  projectId,
  sdataGroup,
  setSdataGroup,
  banners,
  selectedProject,
  selectedClient,
  surveyName,
  surveyFileName,
  user,
  powner,
  list,
  columnIndex,
  autoPreQuotes,
  alphaOrder,
  hideme,
  qtitlehide,
  removebold,
  removinstt,
  quotaData,
  templateOptions = [],
  selectedTemplateValue,
  onExportFilesUpdate,
}) => {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [exportingType, setExportingType] = useState("");
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  const getSelectedTemplate = () => {
    return (
      templateOptions.find((t) => t.value === selectedTemplateValue) ||
      templateOptions[0]
    );
  };

  const trackDownload = async (exportType, success = true, errorMessage = null) => {
    try {
      await fetch(`${apiService.baseURL}/track-download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userEmail: user?.user?.email || user?.email || "unknown@email.com",
          projectId,
          projectName: selectedProject?.name || "Unknown Project",
          clientName: selectedClient?.name || "Unknown Client",
          surveyName: surveyName || surveyFileName || "Untitled Survey",
          exportType,
          timestamp: new Date().toISOString(),
          success,
          errorMessage,
        }),
      });
    } catch (error) {
      console.error("Error tracking download:", error);
    }
  };

  const handleExport = async (exportType, additionalOptions = {}) => {
    setIsExporting(true);
    setExportingType(exportType);

    setSdataGroup((prevDataGroup) => {
      const updatedDataGroup = { ...prevDataGroup };

      const requestBody = {
        projectId,
        dataList: updatedDataGroup,
        banners,
        project: JSON.stringify(selectedProject),
        client: JSON.stringify(selectedClient),
        list,
        type: exportType,
        columnIndex,
        autoPreQuotes,
        alphaOrder,
        hideme,
        qtitlehide,
        removebold,
        removinstt,
        user: powner,
        quotaData,
        ...additionalOptions,
      };

      if (exportType === "confirmit") {
        const selectedTemplate = getSelectedTemplate();
        requestBody.templateName = selectedTemplate.value;
        requestBody.insertionPoint = selectedTemplate.columns;
        requestBody.listPoint = selectedTemplate.lstcol;
      }

      fetch(`${apiService.baseURL}/generate-export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(requestBody),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/zip")) {
            return response.blob().then((blob) => ({ blob, isZip: true }));
          } else if (contentType && contentType.includes("application/json")) {
            return response.json();
          } else {
            return response.text();
          }
        })
        .then((data) => {
          trackDownload(exportType, true, null);

          if (data && data.isZip) {
            const link = document.createElement("a");
            link.href = URL.createObjectURL(data.blob);
            link.download = `${surveyName || surveyFileName || "survey"}_${exportType}.zip`;
            link.click();
            toast.success("Confirmit zip file saved successfully!");
            return;
          } else if (exportType === "decipher") {
            const blob = new Blob(
              [typeof data === "string" ? data : JSON.stringify(data)],
              { type: "text/xml" }
            );
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${surveyName || surveyFileName || "survey"}_decipher.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success("Decipher XML file saved successfully!");
            return;
          }

          const fileExtensions = {
            sawtooth: "SSI",
            pollstudio: "txt",
            confirmit: "xml",
            decipher: "xml",
            qualtrics: "qsf",
            dimension: "txt",
            dimensionRoute: "txt",
            intellisurvey: "txt",
          };

          const successMessages = {
            sawtooth: "Sawtooth file saved successfully!",
            pollstudio: "Poll Studio file saved successfully!",
            confirmit: "Confirmit file saved successfully!",
            decipher: "Decipher file saved successfully!",
            qualtrics: "Qualtrics file saved successfully!",
            dimension: "Dimension file saved successfully!",
            dimensionRoute: "Dimension routes file saved successfully!",
            intellisurvey: "Intelli Survey file saved successfully!",
          };

          const exportData = {
            content: data,
            type: exportType,
            extension: fileExtensions[exportType] || "txt",
            timestamp: new Date().toISOString(),
            fileName: `${surveyName || surveyFileName || "survey"}_${exportType}.${fileExtensions[exportType] || "txt"}`,
          };

          onExportFilesUpdate?.(exportType, exportData);
          toast.success(successMessages[exportType] || "File exported successfully!");
        })
        .catch((error) => {
          trackDownload(exportType, false, error.message);
          toast.error(`Failed to export ${exportType}. Please try again.`);
        })
        .finally(() => {
          setIsExporting(false);
          setExportingType("");
        });

      return updatedDataGroup;
    });
  };

  const createDecipherSurvey = async () => {
    setSdataGroup((prevDataGroup) => {
      const updatedDataGroup = { ...prevDataGroup };

      const makeDecipherRequests = async () => {
        try {
          const baseSurveyName =
            surveyName || surveyFileName?.replace(/\.[^/.]+$/, "") || "My Test Survey";
          const versionNumber = String(Math.floor(Math.random() * 900) + 100);
          const actualSurveyName = `${baseSurveyName} v${versionNumber}`;
          const surveyPath =
            baseSurveyName.toLowerCase().replace(/[^a-zA-Z0-9_]/g, "_") +
            "_v" +
            versionNumber +
            "_" +
            Date.now();

          const createResponse = await fetch(`${apiService.baseURL}/decipher/create-survey`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: actualSurveyName,
              ptype: "beacon",
              category: "internet",
              description: `Survey created from ${baseSurveyName} (Version ${versionNumber})`,
              prefPath: surveyPath,
            }),
          });

          if (!createResponse.ok) {
            const errorData = await createResponse.text();
            throw new Error(`Create survey failed: ${createResponse.status} - ${errorData}`);
          }

          const createResult = await createResponse.json();
          toast.success("Successfully saved the file");

          const generateResponse = await fetch(`${apiService.baseURL}/generate-titles`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              projectId,
              dataList: updatedDataGroup,
              banners,
              project: JSON.stringify(selectedProject),
              client: JSON.stringify(selectedClient),
              columnIndex,
            }),
          });

          if (!generateResponse.ok) {
            const errorData = await generateResponse.text();
            throw new Error(`Generate titles failed: ${generateResponse.status} - ${errorData}`);
          }

          const generatedData = await generateResponse.text();

          const uploadResponse = await fetch(`${apiService.baseURL}/decipher/upload-survey`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ surveyPath, xmlData: generatedData }),
          });

          if (!uploadResponse.ok) {
            const errorData = await uploadResponse.text();
            throw new Error(`Upload to Decipher failed: ${uploadResponse.status} - ${errorData}`);
          }

          const uploadResult = await uploadResponse.json();
          return { createResult, generatedData, uploadResult, surveyName: actualSurveyName, surveyPath, versionNumber };
        } catch (error) {
          console.error("Decipher API Error:", error);
          throw error;
        }
      };

      makeDecipherRequests()
        .then((results) => {
          trackDownload("decipher", true, null);
          toast.success(`Your survey "${results.surveyName}" successfully saved to Decipher!`);
        })
        .catch((error) => {
          trackDownload("decipher", false, error.message);
          let errorMessage = "Failed to save survey to Decipher";
          if (error.message) {
            if (error.message.includes("already exists")) {
              errorMessage = "Survey name already exists in Decipher. Please try again.";
            } else if (error.message.includes("400")) {
              errorMessage = "Invalid survey data. Please check your configuration.";
            } else if (error.message.includes("401") || error.message.includes("403")) {
              errorMessage = "Authentication error. Please check your Decipher credentials.";
            } else {
              errorMessage = `Error: ${error.message}`;
            }
          }
          toast.error(errorMessage);
        });

      return updatedDataGroup;
    });
  };

  return (
    <>
      <div className="export-dropdown-wrapper">
        <button
          type="button"
          className="export-dropdown-trigger"
          onClick={() => setShowExportDropdown((v) => !v)}
          title="Export options"
        >
          <FontAwesomeIcon icon={faFileExport} />
          <span className="export-trigger-label">Export</span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`export-trigger-caret ${showExportDropdown ? "open" : ""}`}
          />
        </button>

        {showExportDropdown && (
          <>
            <div
              className="export-dropdown-backdrop"
              onClick={() => setShowExportDropdown(false)}
            />
            <div className="export-dropdown-panel">
              <div className="export-dropdown-header">
                <div className="export-dropdown-header-left">
                  <FontAwesomeIcon icon={faFileExport} style={{ marginRight: "8px" }} />
                  Export Survey As
                </div>
                <div className="export-dropdown-header-right">
                  <button className="export-dropdown-close-btn" onClick={() => setShowExportDropdown(false)} title="Close">
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              </div>
              <div className="export-dropdown-grid">
                {features.sawtooth && (
                  <button
                    className="export-option-btn"
                    onClick={() => { handleExport("sawtooth"); setShowExportDropdown(false); }}
                    title="Export Sawtooth SSI file"
                  >
                    <FontAwesomeIcon icon={faFileExport} /> <span>ST</span>
                    <small>Sawtooth</small>
                  </button>
                )}
                {features.pollstudio && (
                  <button
                    className="export-option-btn"
                    onClick={() => { handleExport("pollstudio"); setShowExportDropdown(false); }}
                    title="Export Poll Studio JSON"
                  >
                    <FontAwesomeIcon icon={faFileExport} /> <span>PS</span>
                    <small>Poll Studio</small>
                  </button>
                )}
                {features.confirmit && (
                  <button
                    className="export-option-btn"
                    onClick={() => { handleExport("confirmit"); setShowExportDropdown(false); }}
                    title="Export Confirmit XML"
                  >
                    <FontAwesomeIcon icon={faFileExport} /> <span>CF</span>
                    <small>Confirmit</small>
                  </button>
                )}
                {features.decipher_xml && (
                  <button
                    className="export-option-btn"
                    onClick={() => { handleExport("decipher"); setShowExportDropdown(false); }}
                    title="Export Decipher XML"
                  >
                    <FontAwesomeIcon icon={faCode} /> <span>DCXML</span>
                    <small>Decipher XML</small>
                  </button>
                )}
                {features.decipher && (
                  <button
                    className="export-option-btn"
                    onClick={() => { createDecipherSurvey(); setShowExportDropdown(false); }}
                    title="Create Decipher survey directly"
                  >
                    <FontAwesomeIcon icon={faCube} /> <span>DC</span>
                    <small>Decipher</small>
                  </button>
                )}
                {features.dimension && (
                  <button
                    className="export-option-btn"
                    onClick={() => { handleExport("dimension"); setShowExportDropdown(false); }}
                    title="Export Dimensions MDD file"
                  >
                    <FontAwesomeIcon icon={faFileExport} /> <span>DM</span>
                    <small>Dimensions</small>
                  </button>
                )}
                {features.qualtrics && (
                  <button
                    className="export-option-btn"
                    onClick={() => { handleExport("qualtrics"); setShowExportDropdown(false); }}
                    title="Export Qualtrics QSF file"
                  >
                    <FontAwesomeIcon icon={faFileExport} /> <span>QT</span>
                    <small>Qualtrics</small>
                  </button>
                )}
                {features.intelli && (
                  <button
                    className="export-option-btn"
                    onClick={() => { handleExport("intelli"); setShowExportDropdown(false); }}
                    title="Export Intelli Survey file"
                  >
                    <FontAwesomeIcon icon={faFileExport} /> <span>IS</span>
                    <small>Intelli Survey</small>
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {isExporting && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
        >
          <div className="export-loader" />
          <p
            style={{
              color: "white",
              marginTop: "20px",
              fontSize: "18px",
              fontWeight: "600",
              textTransform: "capitalize",
            }}
          >
            Exporting {exportingType}...
          </p>
        </div>
      )}
    </>
  );
};

export default ExportOptionsDropdown;
